import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, CircularProgress, Link, Typography } from '@mui/material';
import CoverageApi, { CourtCoverageDto } from '../../api/CoverageApi';

interface AttorneySummary {
  guid: string;
  name: string;
  appearances: number;
  lastOn: string;
}

/** One line per attorney, most recent first, from the court's coverage history. */
function summarise(rows: CourtCoverageDto[]): AttorneySummary[] {
  const byAttorney = new Map<string, AttorneySummary>();
  for (const row of rows) {
    if (!row.attorneyGuid) continue;
    const current = byAttorney.get(row.attorneyGuid);
    if (current) {
      current.appearances += 1;
      if (row.CourtCoverage_CoveredOn > current.lastOn) current.lastOn = row.CourtCoverage_CoveredOn;
    } else {
      byAttorney.set(row.attorneyGuid, {
        guid: row.attorneyGuid,
        name: row.attorneyName ?? 'Unnamed attorney',
        appearances: 1,
        lastOn: row.CourtCoverage_CoveredOn,
      });
    }
  }
  return [...byAttorney.values()].sort((a, b) => b.lastOn.localeCompare(a.lastOn));
}

/**
 * "The court page will link to its prosecutors and its covering attorneys" (6 Oct 2026). The
 * prosecutors are already on the page; this lists who has covered here, from recorded appearances.
 */
const CourtCoveringAttorneys: React.FC<{ courtGuid: string }> = ({ courtGuid }) => {
  const [attorneys, setAttorneys] = useState<AttorneySummary[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    CoverageApi.listCoverage({ courtGuid })
      .then((rows) => !cancelled && setAttorneys(summarise(rows)))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [courtGuid]);

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Could not load the covering attorneys.
      </Typography>
    );
  }
  if (!attorneys) return <CircularProgress size={20} />;
  if (attorneys.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nobody has a recorded appearance here yet.{' '}
        <Link component={RouterLink} to="/covering-attorneys">
          Covering attorneys
        </Link>
      </Typography>
    );
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {attorneys.map((a) => (
        <Chip
          key={a.guid}
          size="small"
          variant="outlined"
          component={RouterLink}
          to="/covering-attorneys"
          clickable
          label={`${a.name} · ${a.appearances} appearance${a.appearances === 1 ? '' : 's'}, last ${a.lastOn}`}
        />
      ))}
    </Box>
  );
};

export default CourtCoveringAttorneys;
